import * as _ from 'lodash';
import ColorRenderer from './utilities/color_renderer';
// Some colors are taken straight from the config file
import { ConfigService } from 'app/config/config.service';
// RGB values
import * as imsColor from 'app/shared/utilities/ims_color';
import { ColorConfig } from 'app/shared/collections/StatDefinition';

// Just putting this here because connectivity is a huge edge case
const CONNECTIVITY_FIELD = 'connectivity';

export class ColorRendererService {
    _rendererSpec: { [name: string]: ColorConfig };

    /**
     * The configService is actually the ConfigService class where all config options are just mashed on.
     * @param {ConfigService} configService
     */
    constructor(configService: ConfigService) {
        /**
         * A temporary solution for doing color remapping.
         *
         * The map uses a certain palette for colors while the dashboard and aggregate view
         * uses a different set of colors.
         *
         * We should somehow standardize this coloring scheme in one of 3 ways:
         * 1. Everything is defined in map colors and when non map widgets require a
         * config, the colors are remapped similar to below.
         * 2. Everything is defined in non map colors and when map widgets require a config,
         * the colors are remapped similar to below.
         * 3. Everything is defined in a usage agnostic fashion and remapped according to
         * whatever widget requests a config.
         */

        /**
         * Currently all hardware stats with percentage values use the same
         * color renderer config
         * @type {{}}
         */
        const hardwareStatConfig = configService.hardwareStatColorRendererConfig;

        this._rendererSpec = {
            // Try the legacy setting first
            avg_rssi:
                configService.rssiMapColorRendererConfig || configService.rssiColorRendererConfig,
            avg_rsrp: configService.rsrpColorRendererConfig,
            avg_rsrq: configService.rsrqColorRendererConfig,
            avg_sinr: configService.sinrColorRendererConfig,
            cost: configService.costColorRendererConfig,
            battery_temp: configService.solarControllerBatteryTempColorRendererConfig,
            // Try the legacy setting first
            battery_voltage:
                configService.solarControllerBatteryVoltageColorConfig ||
                configService.solarControllerBatteryVoltageColorRendererConfig,
            panel_voltage: configService.solarControllerPanelVoltageColorRendererConfig,
            avg_tx_rate: configService.txRateColorRendererConfig,
            avg_rx_rate: configService.rxRateColorRendererConfig,
            snr: configService.snrColorRendererConfig,
            latency: configService.latencyColorRendererConfig,
            [CONNECTIVITY_FIELD]: configService.connectivityColorRendererConfig,
            noise: configService.noiseColorRendererConfig,
            channel_utilisation: configService.channelUtilisationColorRendererConfig,
            total_cpu_percentage: hardwareStatConfig,
            total_ram_percentage: hardwareStatConfig,
            total_storage_percentage: hardwareStatConfig,
            ram_used_percentage: hardwareStatConfig,
            cpu_percentage: hardwareStatConfig,
            storage_used_percentage: hardwareStatConfig,
        };
    }

    /**
     * Returns a copy of the color config for a given type
     * @param {string} type
     * @returns {ColorConfig}
     */
    colorRendererConfigFor(type: string): ColorConfig {
        return _.cloneDeep(this._rendererSpec[type]);
    }

    /**
     * Get a color renderer for a type of value e.g. avg_rssi
     * @param type a type of value e.g. avg_rssi
     * @param multiplier multiply all the point values in the colour config. Used as a hack to get around the
     * connectivity output difference from the raw row and the aggregate end point
     * TODO: make consistent somehow!
     * @returns {*} A ColorRenderer object
     */
    colorRendererFor(type: string, multiplier?: number): ColorRenderer | null {
        if (_.has(this._rendererSpec, type)) {
            const spec = this.colorRendererConfigFor(type);
            if (spec) {
                if (!_.isNil(multiplier)) {
                    if (Array.isArray(spec.points)) {
                        spec.points.forEach((p) => {
                            if (_.isNumber(p.value)) {
                                p.value = (p.value as any) * multiplier;
                            }
                        });
                    }
                }
                return new ColorRenderer(spec);
            } else {
                // For some reason the spec is falsey. Just color everything white
                return new ColorRenderer({
                    points: [
                        {
                            color: imsColor.IMS_BLUE,
                            value: 0,
                        },
                    ],
                    default: imsColor.IMS_WHITE,
                    type: 'traffic_light',
                });
            }
        } else {
            return null;
        }
    }

    /**
     * Add/update a color renderer for a type of field e.g. avg_rssi
     * @param fieldName a type of field e.g. avg_rssi
     * @param colorSpec The colorspec for that field, {points, type, default} object
     * @returns {*} A ColorRenderer object
     */
    setColorRenderer(fieldName: string, colorSpec: ColorConfig): void {
        this._rendererSpec[fieldName] = colorSpec;
    }
}
